// NetSave AI — the Claude panel inside the CRM.
//
// Two modes, fixed for a session's life:
//   agent — reads and acts on CRM data through the same tools the MCP server
//           exposes, with sends and status changes behind a confirmation card
//   chat  — general purpose, no tools, no CRM access
//
// Renders in a slide-over panel or full page; the only difference is chrome.
// Everything streams over NDJSON from the Cloud Run service, so the backend is
// the only thing holding an API key.

import { useCallback, useEffect, useRef, useState } from "react";
import { BRAND } from "../../config/constants";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { btnP, btnS } from "../../utils/styles";
import * as api from "../../utils/aiApi";
import Markdown, { CopyButton } from "./Markdown";

const MODE_META = {
  agent: {
    label: "CRM Agent",
    blurb: "Reads and acts on CRM data. Sends need your approval.",
    colour: BRAND.blue,
  },
  chat: {
    label: "Chat",
    blurb: "General purpose. No CRM access.",
    colour: "#7F77DD",
  },
};

export default function NetSaveAI({ theme, t, mobile, clientId = null, onClose, fullPage = false }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);

  const [loadingList, setLoadingList] = useState(true);
  const [loadingSession, setLoadingSession] = useState(false);
  const [error, setError] = useState("");

  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [liveText, setLiveText] = useState("");
  const [liveTools, setLiveTools] = useState([]);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [showSidebar, setShowSidebar] = useState(!mobile);
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState("");

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);

  // ── Loading ───────────────────────────────────────────────────────────────

  const refreshList = useCallback(async () => {
    try {
      setSessions(await api.listSessions());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingSession(true);
    setError("");
    api
      .getSession(sessionId)
      .then((d) => {
        if (cancelled) return;
        setSession(d.session);
        setMessages(d.messages);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoadingSession(false));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Keep the newest message in view while a reply streams in.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, liveText, liveTools, pendingConfirm]);

  // ── Sending ───────────────────────────────────────────────────────────────

  const consumeEvents = useCallback(
    (event) => {
      switch (event.type) {
        case "text":
          setLiveText((prev) => prev + event.text);
          break;
        case "tool_start":
          setLiveTools((prev) => [
            ...prev,
            { id: event.id, tool: event.tool, args: event.args, running: true },
          ]);
          break;
        case "tool_end":
          setLiveTools((prev) =>
            prev.map((x) =>
              x.id === event.id
                ? { ...x, running: false, ok: event.ok, summary: event.summary, draftId: event.draftId }
                : x
            )
          );
          break;
        case "confirm_required":
          setPendingConfirm({
            draftId: event.draftId,
            tool: event.tool,
            title: event.title,
            args: event.args,
          });
          break;
        case "summarised":
          setNotice(`Earlier messages were summarised to stay within context (${event.dropped} folded).`);
          break;
        case "limit":
          setNotice(event.message);
          break;
        case "title":
          setSession((s) => (s ? { ...s, title: event.title } : s));
          setSessions((list) =>
            list.map((s) => (s.id === sessionId ? { ...s, title: event.title } : s))
          );
          break;
        case "stopped":
          setNotice("Stopped.");
          break;
        case "error":
          setError(event.message);
          break;
        default:
          break;
      }
    },
    [sessionId]
  );

  /** Reload the transcript from the server — it is the record, not local state. */
  const settle = useCallback(async (id) => {
    setLiveText("");
    setLiveTools([]);
    setStreaming(false);
    abortRef.current = null;
    try {
      const d = await api.getSession(id);
      setSession(d.session);
      setMessages(d.messages);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const send = useCallback(
    async (text, targetSessionId = sessionId) => {
      if (!text.trim() || streaming || !targetSessionId) return;
      setError("");
      setNotice("");
      setPendingConfirm(null);
      setStreaming(true);
      setLiveText("");
      setLiveTools([]);

      // Show it immediately rather than waiting for the round trip.
      setMessages((prev) => [
        ...prev,
        { id: `local-${Date.now()}`, role: "user", content: [{ type: "text", text }], local: true },
      ]);

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        await api.sendMessage(targetSessionId, text, consumeEvents, abort.signal);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      }
      await settle(targetSessionId);
      refreshList();
    },
    [sessionId, streaming, consumeEvents, settle, refreshList]
  );

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await send(text);
  };

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const decide = async (decision) => {
    const card = pendingConfirm;
    if (!card || !sessionId) return;
    setPendingConfirm(null);
    setStreaming(true);
    setLiveText("");
    setLiveTools([]);

    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await api.confirmAction(sessionId, card.draftId, decision, consumeEvents, abort.signal);
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message);
    }
    await settle(sessionId);
  };

  // ── Session actions ───────────────────────────────────────────────────────

  const startSession = async (mode) => {
    setError("");
    try {
      const created = await api.createSession({ mode, clientId });
      setSessions((prev) => [created, ...prev]);
      setSessionId(created.id);
      setSession(created);
      setMessages([]);
      if (mobile) setShowSidebar(false);
      setTimeout(() => composerRef.current?.focus(), 50);
    } catch (e) {
      setError(e.message);
    }
  };

  const rename = async (id) => {
    const title = renameText.trim();
    setRenaming(null);
    if (!title) return;
    try {
      await api.patchSession(id, { title });
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
      if (id === sessionId) setSession((s) => ({ ...s, title }));
    } catch (e) {
      setError(e.message);
    }
  };

  const archive = async (id) => {
    try {
      await api.patchSession(id, { archived: true });
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (id === sessionId) setSessionId(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const destroy = async (id) => {
    const s = sessions.find((x) => x.id === id);
    if (!window.confirm(`Delete "${s?.title || "this conversation"}"? This cannot be undone.`)) return;
    try {
      await api.removeSession(id);
      setSessions((prev) => prev.filter((x) => x.id !== id));
      if (id === sessionId) setSessionId(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const switchModel = async (model) => {
    try {
      await api.patchSession(sessionId, { model });
      setSession((s) => ({ ...s, model }));
    } catch (e) {
      setError(e.message);
    }
  };

  /** Drop the last assistant turn and re-ask with the same user message. */
  const regenerate = async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user" && isPlainUser(m));
    if (!lastUser) return;
    await send(textOf(lastUser));
  };

  const editLast = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user" && isPlainUser(m));
    if (!lastUser) return;
    setDraft(textOf(lastUser));
    composerRef.current?.focus();
  };

  const conversationMarkdown = () =>
    messages
      .filter((m) => isPlainUser(m) || m.role === "assistant")
      .map((m) => `## ${m.role === "user" ? "Me" : "NetSave AI"}\n\n${textOf(m)}`)
      .join("\n\n---\n\n");

  // ── Render ────────────────────────────────────────────────────────────────

  const visible = sessions.filter((s) =>
    !search.trim() ? true : (s.title || "").toLowerCase().includes(search.toLowerCase())
  );

  const mode = session?.mode;
  const meta = mode ? MODE_META[mode] : null;

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        background: t.bg,
        fontFamily: "'Outfit',sans-serif",
      }}
    >
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      {showSidebar && (
        <div
          style={{
            width: mobile ? "100%" : 232,
            flexShrink: 0,
            borderRight: mobile ? "none" : `1px solid ${t.border}`,
            display: "flex",
            flexDirection: "column",
            background: t.surface,
            minHeight: 0,
          }}
        >
          <div style={{ padding: "12px 12px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => startSession("agent")}
                style={{ ...btnP, flex: 1, padding: "8px 10px", fontSize: 12, justifyContent: "center" }}
                title={MODE_META.agent.blurb}
              >
                <Icon name="plus" size={13} color="#fff" /> Agent
              </button>
              <button
                onClick={() => startSession("chat")}
                style={{
                  ...btnS(t),
                  flex: 1,
                  padding: "8px 10px",
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                }}
                title={MODE_META.chat.blurb}
              >
                <Icon name="plus" size={13} /> Chat
              </button>
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
              style={{
                background: t.inputBg,
                border: `1px solid ${t.border}`,
                borderRadius: 7,
                padding: "6px 10px",
                color: t.text,
                fontSize: 12,
                outline: "none",
                fontFamily: "'Outfit',sans-serif",
              }}
            />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 8px", minHeight: 0 }}>
            {loadingList ? (
              <div style={{ padding: 20, textAlign: "center" }}>
                <MiniSpinner size={16} color={BRAND.blue} />
              </div>
            ) : visible.length === 0 ? (
              <div style={{ padding: "18px 10px", fontSize: 12, color: t.textMuted, textAlign: "center" }}>
                {search ? "Nothing matches." : "No conversations yet."}
              </div>
            ) : (
              visible.map((s) => {
                const active = s.id === sessionId;
                return (
                  <div
                    key={s.id}
                    onClick={() => {
                      setSessionId(s.id);
                      if (mobile) setShowSidebar(false);
                    }}
                    style={{
                      padding: "8px 9px",
                      borderRadius: 7,
                      marginBottom: 2,
                      cursor: "pointer",
                      background: active ? BRAND.blue + "15" : "transparent",
                      border: `1px solid ${active ? BRAND.blue + "35" : "transparent"}`,
                    }}
                  >
                    {renaming === s.id ? (
                      <input
                        autoFocus
                        value={renameText}
                        onChange={(e) => setRenameText(e.target.value)}
                        onBlur={() => rename(s.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") rename(s.id);
                          if (e.key === "Escape") setRenaming(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "100%",
                          background: t.inputBg,
                          border: `1px solid ${BRAND.blue}`,
                          borderRadius: 5,
                          padding: "3px 6px",
                          color: t.text,
                          fontSize: 12.5,
                          outline: "none",
                          fontFamily: "'Outfit',sans-serif",
                        }}
                      />
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: active ? 700 : 500,
                            color: active ? BRAND.blue : t.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s.title || "Untitled"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginTop: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              color: MODE_META[s.mode]?.colour || t.textMuted,
                            }}
                          >
                            {MODE_META[s.mode]?.label || s.mode}
                          </span>
                          <span style={{ fontSize: 10, color: t.textMuted }}>
                            {shortDate(s.updatedAt)}
                          </span>
                          {active && (
                            <span style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
                              <IconBtn
                                t={t}
                                icon="edit"
                                title="Rename"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenameText(s.title || "");
                                  setRenaming(s.id);
                                }}
                              />
                              <IconBtn
                                t={t}
                                icon="download"
                                title="Archive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archive(s.id);
                                }}
                              />
                              <IconBtn
                                t={t}
                                icon="trash"
                                title="Delete"
                                danger
                                onClick={(e) => {
                                  e.stopPropagation();
                                  destroy(s.id);
                                }}
                              />
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ── Conversation ────────────────────────────────────────────────── */}
      {(!mobile || !showSidebar) && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderBottom: `1px solid ${t.border}`,
              background: t.surface,
              flexShrink: 0,
            }}
          >
            {mobile && (
              <IconBtn t={t} icon="pipeline" title="Conversations" onClick={() => setShowSidebar(true)} />
            )}
            {!mobile && !showSidebar && (
              <IconBtn t={t} icon="pipeline" title="Show conversations" onClick={() => setShowSidebar(true)} />
            )}
            {!mobile && showSidebar && (
              <IconBtn t={t} icon="x" title="Hide conversations" onClick={() => setShowSidebar(false)} />
            )}

            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: t.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {session ? session.title || "Untitled" : "NetSave AI"}
              </div>
              {meta && (
                <div style={{ fontSize: 10.5, color: meta.colour, fontWeight: 600 }}>
                  {meta.label}
                  {session?.clientId ? " · client linked" : ""}
                </div>
              )}
            </div>

            {session && (
              <select
                value={session.model}
                onChange={(e) => switchModel(e.target.value)}
                disabled={streaming}
                title="Model for this conversation"
                style={{
                  background: t.inputBg,
                  border: `1px solid ${t.border}`,
                  borderRadius: 6,
                  padding: "4px 6px",
                  color: t.textSub,
                  fontSize: 11,
                  outline: "none",
                  fontFamily: "'Outfit',sans-serif",
                  maxWidth: 130,
                }}
              >
                <option value="claude-sonnet-5-5">Sonnet 5.5</option>
                <option value="claude-opus-5-5">Opus 5.5</option>
              </select>
            )}

            {messages.length > 0 && (
              <CopyButton text={conversationMarkdown()} t={t} label="Copy all" />
            )}
            {onClose && <IconBtn t={t} icon="x" title="Close" onClick={onClose} />}
          </div>

          {/* Transcript */}
          <div
            ref={scrollRef}
            style={{ flex: 1, overflowY: "auto", padding: mobile ? "12px" : "16px 20px", minHeight: 0 }}
          >
            {!session ? (
              <EmptyState t={t} onStart={startSession} clientId={clientId} />
            ) : loadingSession ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 30 }}>
                <MiniSpinner size={18} color={BRAND.blue} />
              </div>
            ) : (
              <>
                {mode === "chat" && (
                  <div
                    style={{
                      fontSize: 11,
                      color: t.textMuted,
                      background: t.surfaceAlt,
                      border: `1px solid ${t.border}`,
                      borderRadius: 7,
                      padding: "7px 11px",
                      marginBottom: 14,
                      lineHeight: 1.5,
                    }}
                  >
                    Chat mode has no CRM access. What you type here is sent to Anthropic's
                    API to generate a reply.
                  </div>
                )}

                {messages.length === 0 && (
                  <div style={{ padding: "26px 10px", textAlign: "center", color: t.textMuted, fontSize: 13 }}>
                    {mode === "agent"
                      ? "Ask about a client, a task, an invoice — or tell me what to do."
                      : "Ask anything."}
                  </div>
                )}

                {messages.map((m, i) => (
                  <MessageRow key={m.id || i} message={m} t={t} />
                ))}

                {(liveText || liveTools.length > 0) && (
                  <div style={{ marginBottom: 16 }}>
                    {liveTools.map((x) => (
                      <ToolChip key={x.id} chip={x} t={t} />
                    ))}
                    {liveText && <Markdown t={t}>{liveText}</Markdown>}
                  </div>
                )}

                {streaming && !liveText && liveTools.length === 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: t.textMuted, fontSize: 12.5 }}>
                    <MiniSpinner size={13} color={BRAND.blue} /> Thinking…
                  </div>
                )}

                {pendingConfirm && (
                  <ConfirmCard card={pendingConfirm} t={t} onDecide={decide} busy={streaming} />
                )}

                {notice && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 11.5,
                      color: t.textMuted,
                      fontStyle: "italic",
                      textAlign: "center",
                    }}
                  >
                    {notice}
                  </div>
                )}
              </>
            )}

            {error && (
              <div
                style={{
                  marginTop: 12,
                  padding: "9px 12px",
                  borderRadius: 7,
                  background: "#f8717112",
                  border: "1px solid #f8717130",
                  color: "#f87171",
                  fontSize: 12.5,
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          {session && (
            <div style={{ borderTop: `1px solid ${t.border}`, padding: "10px 14px", background: t.surface, flexShrink: 0 }}>
              {messages.length > 1 && !streaming && (
                <div style={{ display: "flex", gap: 6, marginBottom: 7 }}>
                  <TinyBtn t={t} onClick={regenerate}>
                    <Icon name="refresh" size={11} /> Regenerate
                  </TinyBtn>
                  <TinyBtn t={t} onClick={editLast}>
                    <Icon name="edit" size={11} /> Edit last
                  </TinyBtn>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  ref={composerRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  rows={1}
                  placeholder={streaming ? "Working…" : "Message NetSave AI    (Enter to send)"}
                  disabled={streaming}
                  style={{
                    flex: 1,
                    resize: "none",
                    maxHeight: 150,
                    minHeight: 38,
                    background: t.inputBg,
                    border: `1px solid ${t.border}`,
                    borderRadius: 9,
                    padding: "9px 12px",
                    color: t.text,
                    fontSize: 13,
                    lineHeight: 1.5,
                    outline: "none",
                    fontFamily: "'Outfit',sans-serif",
                  }}
                />
                {streaming ? (
                  <button
                    onClick={stop}
                    style={{ ...btnS(t), padding: "9px 14px", display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <Icon name="x" size={13} /> Stop
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={!draft.trim()}
                    style={{ ...btnP, padding: "9px 15px", opacity: draft.trim() ? 1 : 0.45 }}
                  >
                    <Icon name="send" size={13} color="#fff" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function MessageRow({ message, t }) {
  const isUser = message.role === "user";

  // A user-role message can also carry tool_result blocks, which the loop writes
  // back to the transcript. Those are machine traffic, shown as chips instead.
  const toolResults = blocks(message).filter((b) => b.type === "tool_result");
  if (isUser && toolResults.length > 0 && !hasText(message)) return null;

  const systemNote = isUser && textOf(message).startsWith("[system]");
  if (systemNote) {
    return (
      <div
        style={{
          margin: "0 0 14px",
          fontSize: 11.5,
          color: t.textMuted,
          fontStyle: "italic",
          padding: "6px 10px",
          borderLeft: `2px solid ${t.border}`,
        }}
      >
        {textOf(message).replace(/^\[system\]\s*/, "")}
      </div>
    );
  }

  const toolUses = blocks(message).filter((b) => b.type === "tool_use");
  const body = textOf(message);

  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div
          style={{
            maxWidth: "82%",
            background: BRAND.blue + "15",
            border: `1px solid ${BRAND.blue}30`,
            borderRadius: "10px 10px 2px 10px",
            padding: "9px 12px",
            fontSize: 13.5,
            lineHeight: 1.6,
            color: t.text,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 18 }}>
      {toolUses.map((b) => (
        <ToolChip key={b.id} chip={{ id: b.id, tool: b.name, args: b.input, running: false, ok: true }} t={t} />
      ))}
      {body && <Markdown t={t}>{body}</Markdown>}
      {body && (
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <CopyButton text={body} t={t} />
        </div>
      )}
    </div>
  );
}

/** Collapsible record of one tool call. Collapsed by default: the arguments can
 *  name a client, and the transcript is read over shoulders. */
function ToolChip({ chip, t }) {
  const [open, setOpen] = useState(false);
  const colour = chip.running ? t.textMuted : chip.ok === false ? "#f87171" : BRAND.green;

  return (
    <div style={{ marginBottom: 6 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 9px",
          borderRadius: 20,
          background: t.surfaceAlt,
          border: `1px solid ${t.border}`,
          color: t.textSub,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "'Outfit',sans-serif",
          maxWidth: "100%",
        }}
      >
        {chip.running ? (
          <MiniSpinner size={10} color={BRAND.blue} />
        ) : (
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: colour, flexShrink: 0 }} />
        )}
        <span style={{ fontFamily: "'Outfit',sans-serif" }}>{humanTool(chip.tool)}</span>
        {chip.summary && (
          <span
            style={{
              color: t.textMuted,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {chip.summary}
          </span>
        )}
      </button>
      {open && (
        <pre
          style={{
            margin: "5px 0 0",
            padding: "8px 10px",
            background: t.inputBg,
            border: `1px solid ${t.border}`,
            borderRadius: 7,
            fontSize: 11,
            color: t.textSub,
            overflowX: "auto",
            fontFamily: "ui-monospace,Menlo,Consolas,monospace",
          }}
        >
          {JSON.stringify(chip.args ?? {}, null, 2)}
        </pre>
      )}
    </div>
  );
}

/**
 * The human gate. Nothing in this card is cosmetic: the action does not exist
 * server-side until Confirm is clicked, and the model has no way to click it.
 */
function ConfirmCard({ card, t, onDecide, busy }) {
  return (
    <div
      style={{
        margin: "6px 0 14px",
        border: `1.5px solid ${BRAND.blue}55`,
        background: BRAND.blue + "0C",
        borderRadius: 10,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <Icon name="warning" size={14} color={BRAND.blue} />
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
          {card.title || humanTool(card.tool)}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: BRAND.blue,
          }}
        >
          Needs your approval
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 11 }}>
        {Object.entries(card.args || {}).map(([k, v]) => (
          <div key={k} style={{ display: "flex", gap: 8, fontSize: 12.5 }}>
            <span style={{ color: t.textMuted, minWidth: 92, flexShrink: 0 }}>{label(k)}</span>
            <span style={{ color: t.text, fontWeight: 600, overflowWrap: "anywhere" }}>
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => onDecide("confirm")}
          disabled={busy}
          style={{ ...btnP, padding: "8px 16px", fontSize: 12.5, opacity: busy ? 0.5 : 1 }}
        >
          <Icon name="check" size={13} color="#fff" /> Confirm
        </button>
        <button
          onClick={() => onDecide("decline")}
          disabled={busy}
          style={{ ...btnS(t), padding: "8px 16px", fontSize: 12.5, opacity: busy ? 0.5 : 1 }}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

function EmptyState({ t, onStart, clientId }) {
  return (
    <div style={{ padding: "34px 16px", textAlign: "center" }}>
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: 12,
          margin: "0 auto 14px",
          background: BRAND.blue + "18",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name="brain" size={24} color={BRAND.blue} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 5 }}>NetSave AI</div>
      <div style={{ fontSize: 12.5, color: t.textMuted, marginBottom: 20, lineHeight: 1.55 }}>
        Pick a mode to start. It is fixed for the conversation.
        {clientId ? " This client will be loaded as context." : ""}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9, maxWidth: 330, margin: "0 auto" }}>
        {["agent", "chat"].map((m) => (
          <button
            key={m}
            onClick={() => onStart(m)}
            style={{
              textAlign: "left",
              padding: "12px 14px",
              borderRadius: 10,
              background: t.surface,
              border: `1.5px solid ${MODE_META[m].colour}40`,
              cursor: "pointer",
              fontFamily: "'Outfit',sans-serif",
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: MODE_META[m].colour }}>
              {MODE_META[m].label}
            </div>
            <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 2, lineHeight: 1.45 }}>
              {MODE_META[m].blurb}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function IconBtn({ t, icon, title, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 4,
        display: "flex",
        alignItems: "center",
        color: danger ? "#f87171" : t.textMuted,
      }}
    >
      <Icon name={icon} size={13} />
    </button>
  );
}

function TinyBtn({ t, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 8px",
        borderRadius: 6,
        background: "transparent",
        border: `1px solid ${t.border}`,
        color: t.textMuted,
        fontSize: 10.5,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Outfit',sans-serif",
      }}
    >
      {children}
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const blocks = (m) => (Array.isArray(m?.content) ? m.content : []);

const hasText = (m) => blocks(m).some((b) => b.type === "text" && b.text?.trim());

const textOf = (m) => {
  if (typeof m?.content === "string") return m.content;
  return blocks(m)
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("\n");
};

/** A message the user actually typed, rather than tool traffic or a system note. */
const isPlainUser = (m) =>
  m.role === "user" && hasText(m) && !textOf(m).startsWith("[system]");

const humanTool = (name) =>
  String(name || "")
    .replace(/^crm_/, "")
    .replace(/^qb_/, "QuickBooks ")
    .replace(/^onedrive_/, "OneDrive ")
    .replace(/^outlook_/, "Outlook ")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const label = (k) =>
  k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
